/**
 * The `/v1/admin/*` client.
 *
 * Unlike `catalogue.ts` — which reads the public endpoints as scaffolding —
 * every call here hits a real admin endpoint that exists, and every one of them
 * is behind `requireAuth` + `requireRole("ADMIN")` on the API's own router.
 * That gate is the API's, not this file's: nothing in the browser is a security
 * boundary, and a panel that hid buttons would still be a panel whose fetches
 * an ordinary customer could replay.
 */
import { apiFetch } from "@/lib/api-client";
import type {
  AdminOrder,
  ApiPayment,
  ApiRefund,
  ApiResponse,
  ApiWebhookEvent,
  OrderStatus,
  Paginated,
  PaymentStatus,
  PaymentSummary,
  RefundStatus,
  ShipmentStatus,
  ShipmentSummary,
  ShippingOptions,
  ApiShipment,
} from "@/lib/api/types";

/** Denser than the storefront's page size — these are tables, not showcases. */
export const PER_PAGE = 25;

type Query = Record<string, string | number | boolean | undefined>;

/** Drops undefined keys so an unset filter is an absent parameter, not `"undefined"`. */
function clean(query: Query): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== ""),
  ) as Record<string, string | number | boolean>;
}

// ─── Orders ──────────────────────────────────────────────────────────────────

export type OrderQuery = {
  page?: number;
  status?: OrderStatus | undefined;
  paymentMethod?: "COD" | "ONLINE" | undefined;
  search?: string | undefined;
};

export function fetchOrders(
  query: OrderQuery = {},
  signal?: AbortSignal,
): Promise<Paginated<AdminOrder[]>> {
  return apiFetch<Paginated<AdminOrder[]>>("/v1/admin/orders", {
    query: clean({
      page: query.page ?? 1,
      limit: PER_PAGE,
      status: query.status,
      paymentMethod: query.paymentMethod,
      search: query.search,
    }),
    ...(signal ? { signal } : {}),
  });
}

export async function fetchOrder(orderNumber: string, signal?: AbortSignal): Promise<AdminOrder> {
  const { data } = await apiFetch<ApiResponse<AdminOrder>>(
    `/v1/admin/orders/${encodeURIComponent(orderNumber)}`,
    { ...(signal ? { signal } : {}) },
  );

  return data;
}

/**
 * Forward only. The API refuses a backwards move, so the UI offering one would
 * just produce a 400 — hence `nextStatus` below, which offers exactly one.
 */
export async function advanceOrder(
  orderNumber: string,
  status: OrderStatus,
  note?: string,
): Promise<AdminOrder> {
  const { data } = await apiFetch<ApiResponse<AdminOrder>>(
    `/v1/admin/orders/${encodeURIComponent(orderNumber)}/status`,
    { method: "PATCH", body: { status, ...(note ? { note } : {}) } },
  );

  return data;
}

/**
 * Cancels and, by default, refunds.
 *
 * `refund: false` exists for the case where the money is being settled some
 * other way and a gateway refund would double-pay. It is deliberately the
 * non-default: an operator cancelling a paid order and *not* refunding it is
 * the surprising choice and should have to be made explicitly.
 */
export async function cancelOrder(
  orderNumber: string,
  reason: string,
  refund = true,
): Promise<AdminOrder> {
  const { data } = await apiFetch<ApiResponse<AdminOrder>>(
    `/v1/admin/orders/${encodeURIComponent(orderNumber)}/cancel`,
    { body: { reason, refund } },
  );

  return data;
}

// ─── Payments ────────────────────────────────────────────────────────────────

export type PaymentQuery = {
  page?: number;
  status?: PaymentStatus | undefined;
  search?: string | undefined;
};

export function fetchPayments(
  query: PaymentQuery = {},
  signal?: AbortSignal,
): Promise<Paginated<ApiPayment[]>> {
  return apiFetch<Paginated<ApiPayment[]>>("/v1/admin/payments", {
    query: clean({
      page: query.page ?? 1,
      limit: PER_PAGE,
      status: query.status,
      search: query.search,
    }),
    ...(signal ? { signal } : {}),
  });
}

export async function fetchPayment(
  gatewayOrderId: string,
  signal?: AbortSignal,
): Promise<ApiPayment> {
  const { data } = await apiFetch<ApiResponse<ApiPayment>>(
    `/v1/admin/payments/${encodeURIComponent(gatewayOrderId)}`,
    { ...(signal ? { signal } : {}) },
  );

  return data;
}

/**
 * A partial or repeat refund, initiated by hand.
 *
 * Distinct from cancelling: this moves money without touching the order's
 * status or its stock, which is what a goodwill credit or a short-shipped line
 * actually is. `amountMinor` omitted means "everything still refundable".
 */
export async function refundPayment(
  gatewayOrderId: string,
  input: { amountMinor?: number; note?: string; speed?: "STANDARD" | "INSTANT" } = {},
): Promise<ApiPayment> {
  const { data } = await apiFetch<ApiResponse<ApiPayment>>(
    `/v1/admin/payments/${encodeURIComponent(gatewayOrderId)}/refund`,
    { body: { speed: input.speed ?? "STANDARD", ...clean({ ...input, speed: undefined }) } },
  );

  return data;
}

export async function fetchSummary(signal?: AbortSignal): Promise<PaymentSummary> {
  const { data } = await apiFetch<ApiResponse<PaymentSummary>>("/v1/admin/payments/summary", {
    ...(signal ? { signal } : {}),
  });

  return data;
}

// ─── Refunds ─────────────────────────────────────────────────────────────────

export function fetchRefunds(
  query: { page?: number; status?: RefundStatus | undefined } = {},
  signal?: AbortSignal,
): Promise<Paginated<ApiRefund[]>> {
  return apiFetch<Paginated<ApiRefund[]>>("/v1/admin/refunds", {
    query: clean({ page: query.page ?? 1, limit: PER_PAGE, status: query.status }),
    ...(signal ? { signal } : {}),
  });
}

/**
 * Re-sends a refund that was booked but never reached the gateway.
 *
 * Safe to press twice: the API returns early when the refund already has a
 * `cfRefundId`, and Cashfree treats our `refundId` as an idempotency key — so
 * the worst case is the original result coming back again.
 */
export async function retryRefund(refundId: string): Promise<ApiRefund> {
  const { data } = await apiFetch<ApiResponse<ApiRefund>>(
    `/v1/admin/refunds/${encodeURIComponent(refundId)}/retry`,
    { body: {} },
  );

  return data;
}

// ─── Webhook log ─────────────────────────────────────────────────────────────

export function fetchWebhooks(
  query: { page?: number; type?: string | undefined; invalid?: boolean | undefined } = {},
  signal?: AbortSignal,
): Promise<Paginated<ApiWebhookEvent[]>> {
  return apiFetch<Paginated<ApiWebhookEvent[]>>("/v1/admin/webhooks", {
    query: clean({
      page: query.page ?? 1,
      limit: PER_PAGE,
      type: query.type,
      invalid: query.invalid === undefined ? undefined : String(query.invalid),
    }),
    ...(signal ? { signal } : {}),
  });
}

export async function fetchWebhook(id: string, signal?: AbortSignal): Promise<ApiWebhookEvent> {
  const { data } = await apiFetch<ApiResponse<ApiWebhookEvent>>(
    `/v1/admin/webhooks/${encodeURIComponent(id)}`,
    { ...(signal ? { signal } : {}) },
  );

  return data;
}

// ─── Shipping ────────────────────────────────────────────────────────────────

/** Courier options and prices for an order, straight from iCarry. */
export async function fetchShippingOptions(
  orderNumber: string,
  signal?: AbortSignal,
): Promise<ShippingOptions> {
  const { data } = await apiFetch<ApiResponse<ShippingOptions>>(
    `/v1/admin/orders/${encodeURIComponent(orderNumber)}/shipping/options`,
    { ...(signal ? { signal } : {}) },
  );

  return data;
}

/**
 * Books the parcel.
 *
 * Note what is NOT sent: weight, dimensions, declared value. Those are computed
 * server-side by `buildParcel` from the products and the order total — an
 * operator typing a weight into a form is how a declaration stops matching the
 * box that ships.
 */
export async function bookShipment(
  orderNumber: string,
  input: { courierId?: string | undefined; mode?: "SURFACE" | "AIR" | undefined } = {},
): Promise<ApiShipment> {
  const { data } = await apiFetch<ApiResponse<ApiShipment>>(
    `/v1/admin/orders/${encodeURIComponent(orderNumber)}/shipment`,
    { body: clean(input) },
  );

  return data;
}

export function fetchShipments(
  query: { page?: number; status?: ShipmentStatus | undefined; search?: string | undefined } = {},
  signal?: AbortSignal,
): Promise<Paginated<ApiShipment[]>> {
  return apiFetch<Paginated<ApiShipment[]>>("/v1/admin/shipments", {
    query: clean({
      page: query.page ?? 1,
      limit: PER_PAGE,
      status: query.status,
      search: query.search,
    }),
    ...(signal ? { signal } : {}),
  });
}

export async function fetchShipmentSummary(signal?: AbortSignal): Promise<ShipmentSummary> {
  const { data } = await apiFetch<ApiResponse<ShipmentSummary>>("/v1/admin/shipments/summary", {
    ...(signal ? { signal } : {}),
  });

  return data;
}

export async function cancelShipment(id: string): Promise<ApiShipment> {
  const { data } = await apiFetch<ApiResponse<ApiShipment>>(
    `/v1/admin/shipments/${encodeURIComponent(id)}/cancel`,
    { body: {} },
  );

  return data;
}

/**
 * Forces one reconcile against the courier.
 *
 * For the operator who is on the phone about a parcel and does not want to wait
 * for the sweeper. It goes through the same single writer the webhook does, so
 * it cannot produce a state the callbacks could not.
 */
export async function syncShipment(id: string): Promise<ApiShipment> {
  const { data } = await apiFetch<ApiResponse<ApiShipment>>(
    `/v1/admin/shipments/${encodeURIComponent(id)}/sync`,
    { body: {} },
  );

  return data;
}

/** Re-sends a DRAFT the courier never received. */
export async function retryShipment(id: string): Promise<ApiShipment> {
  const { data } = await apiFetch<ApiResponse<ApiShipment>>(
    `/v1/admin/shipments/${encodeURIComponent(id)}/retry`,
    { body: {} },
  );

  return data;
}

/**
 * The label, as base64 in the ordinary JSON envelope.
 *
 * Blob-ified here rather than linked to, for two reasons that are both easy to
 * get wrong: the encryption middleware wraps `res.json` only, so a binary
 * response would be the one unencrypted body in the system — and a plain
 * `<a href download>` to the API carries no `Authorization` header.
 */
export async function fetchShipmentLabel(orderNumber: string): Promise<Blob> {
  const { data } = await apiFetch<
    ApiResponse<{ filename: string; contentType: string; contentBase64: string }>
  >(`/v1/admin/orders/${encodeURIComponent(orderNumber)}/shipment/label`);

  const bytes = Uint8Array.from(atob(data.contentBase64), (character) => character.charCodeAt(0));

  return new Blob([bytes], { type: data.contentType });
}

// ─── Presentation helpers ────────────────────────────────────────────────────

/**
 * The one status an order can move to next.
 *
 * Derived from the flow rather than offered as a free choice, because the API
 * only accepts forward moves and a dropdown of all five statuses is four ways
 * to get a 400. `null` means there is nowhere left to go.
 */
const FLOW: OrderStatus[] = ["PENDING", "CONFIRMED", "PACKED", "SHIPPED", "DELIVERED"];

/**
 * `hasShipment` skips SHIPPED, and that is the point of the argument.
 *
 * Booking an AWB and advancing to SHIPPED are one operator intent, so leaving
 * both would let them disagree — an order marked shipped with no parcel, or a
 * parcel moving against an order that still says packed. Once a shipment
 * exists, the courier's own pickup scan is what sets SHIPPED, through
 * `applyShipmentOutcome`, and the button that would race it is not offered.
 */
export function nextStatus(status: OrderStatus, hasShipment = false): OrderStatus | null {
  if (status === "CANCELLED") return null;

  const index = FLOW.indexOf(status);
  if (index < 0 || index >= FLOW.length - 1) return null;

  const next = FLOW[index + 1] ?? null;

  if (next === "SHIPPED" && hasShipment) return null;

  return next;
}

/** Mirrors the API's own rule, so a button is never offered that would 409. */
export const CANCELLABLE: readonly OrderStatus[] = ["PENDING", "CONFIRMED", "PACKED"];
